import React, { useState, useEffect } from 'react'
import styled from '@emotion/styled'
import PropTypes from 'prop-types'
import { Link } from 'react-router-dom'

import { config } from '../../config.js'
import { isPirPaymentEnabled } from '../../util/pirConsts.js'
import { lock } from '../../images/images.js'
import { postSecurityCode, postVerifySecurityCode } from '../../dataClient/apiCalls'
import { CenteredFlexColumn } from '../styles/Containers.js'
import Button, { TextButton } from '../common/Button'
import BodyText, { BoldSpan } from '../styles/Text.js'
import Loading from '../common/Loading'
import { ErrorMessage } from '../common/UserMessage'
import CodeInput from './CodeInput'
import pirTerminology from '../../util/pirTerminology'

const Image = styled.img`
  width: 180px;
  align-items: center;
`

const VerificationContainer = styled(CenteredFlexColumn)`
  gap: 15px;
`

function SecurityCodeForm({ requestId, requestType, secondaryId, recipient, email, status, onVerify = () => {}, isExpiredLink = false }) {
  const [loading, setLoading] = useState(true)
  const [codeError, setCodeError] = useState()
  const [code, setCode] = useState(new Array(6).fill(''))

  const [verifyLoading, setVerifyLoading] = useState(false)
  const [verifyError, setVerifyError] = useState()

  const [validationError, setValidationError] = useState(false)

  async function sendNewCode(generate = false) {
    setCodeError(undefined)
    try {
      await postSecurityCode(requestId, requestType, generate, recipient)
    } catch (e) {
      if (e.response) setCodeError(e.response.data.message)
      console.error('failed to send a security code to the requestor', e)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (!isExpiredLink && status === 'COMPLETED' && email && email !== '') {
      sendNewCode()
    } else {
      setLoading(false)
    }
  }, [isExpiredLink, status, email])

  async function verifyCode() {
    setValidationError(false)
    setVerifyError(undefined)
    const joinedCode = code.join('')

    if (joinedCode.length === 6) {
      try {
        setVerifyLoading(true)
        setVerifyError(undefined)
        const response = await postVerifySecurityCode(requestId, requestType, joinedCode, recipient)

        if (response.data.token) {
          localStorage.setItem('securityToken', response.data.token)
        }

        setVerifyLoading(false)
        onVerify()
      } catch (e) {
        if (e.response) setVerifyError(e.response.data.message)
        setVerifyLoading(false)
        console.error('failed to verify the security code', e)
      }
    } else {
      setValidationError(true)
    }
  }

  if (loading) return <Loading />

  if (status === 'COMPLETED' && isExpiredLink) {
    return (
      <CenteredFlexColumn>
        <Image src={lock} alt="" />
        <BodyText>
          This FOI response link has <BoldSpan>expired</BoldSpan>. Please contact the City at {config.foi.privacyTeamEmail} for assistance.
        </BodyText>
      </CenteredFlexColumn>
    )
  }

  if (!email || email === '') {
    return (
      <CenteredFlexColumn>
        <Image src={lock} />
        <BodyText>Download unavailable, please contact the city for assistance.</BodyText>
      </CenteredFlexColumn>
    )
  }

  return (
    <CenteredFlexColumn>
      <Image src={lock} />
      {requestType === 'pir' && (
        <>
          {status === 'NEW' && isPirPaymentEnabled() && (
            <>
              <BodyText>This {pirTerminology.getRequestShortName()} has not been paid for.</BodyText>
              <Button>
                <Link to={`/payment/${requestId}`}>Complete Payment</Link>
              </Button>
            </>
          )}
          {status === 'NEW' && !isPirPaymentEnabled() && (
            <BodyText>
              This {pirTerminology.getRequestShortName()} is incomplete and not ready for download. An email will be sent to the{' '}
              {pirTerminology.getRequestShortName()} recipients when the {pirTerminology.getRequestShortName()} is ready.
            </BodyText>
          )}
          {['PAID', 'SIGNING', 'READY'].includes(status) && (
            <BodyText>
              This {pirTerminology.getRequestShortName()} is incomplete and not ready for download. An email will be sent to the{' '}
              {pirTerminology.getRequestShortName()} recipients when the {pirTerminology.getRequestShortName()} is ready.
            </BodyText>
          )}
          {status === 'REJECTED' && (
            <BodyText>
              This {pirTerminology.getRequestShortName()} has been cancelled, an email with the reason was sent to the{' '}
              {pirTerminology.getRequestShortName()} recipients.
            </BodyText>
          )}
          {status === 'EXPIRED' && (
            <BodyText>
              This {pirTerminology.getRequestShortName()} has expired as all signatures were not received before the document expired.
              {isPirPaymentEnabled() ? ' A refund has been issued to the requestor.' : ''}
            </BodyText>
          )}
        </>
      )}
      {requestType === 'foi' && (
        <>
          {status !== 'COMPLETED' && (
            <BodyText>This FOI is incomplete and not ready for download. An email will be sent to the FOI recipients when the FOI is ready.</BodyText>
          )}
        </>
      )}

      {status === 'COMPLETED' && (
        <>
          {codeError && <ErrorMessage>There was an error sending a code to your email. Please refresh the page or send a new code.</ErrorMessage>}
          <BodyText>
            A one-time security code has been sent to the requestor&#39;s email address at <BoldSpan>{email}</BoldSpan>. Please enter it here to
            access the response documents pertaining to <BoldSpan>{secondaryId}</BoldSpan>.
          </BodyText>
          <CodeInput code={code} onUpdateCode={e => setCode(e)} />

          <VerificationContainer>
            <Button onClick={verifyCode} loading={verifyLoading}>
              Verify
            </Button>
            {validationError && <ErrorMessage>Please enter a valid 6 character code.</ErrorMessage>}
            {verifyError &&
              (verifyError === 'code invalid' ? (
                <ErrorMessage>The entered code is invalid and may have expired. Please try again.</ErrorMessage>
              ) : (
                <ErrorMessage>There was an error verifying your code. Please try again.</ErrorMessage>
              ))}

            <TextButton onClick={() => sendNewCode(true)} disabled={verifyLoading}>
              Send a new code
            </TextButton>
          </VerificationContainer>
        </>
      )}
    </CenteredFlexColumn>
  )
}

SecurityCodeForm.propTypes = {
  requestId: PropTypes.string.isRequired,
  requestType: PropTypes.oneOf(['pir', 'foi']).isRequired,
  secondaryId: PropTypes.string.isRequired,
  recipient: PropTypes.string.isRequired,
  email: PropTypes.string.isRequired,
  status: PropTypes.string.isRequired,
  onVerify: PropTypes.func,
  isExpiredLink: PropTypes.bool
}

export default SecurityCodeForm
